import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { isSelected } from 'helpers';
import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import VisibilityWrapper from 'utils/wrappers/VisibilityWrapper';

import PlanningAlgorithmCard from 'ui/PlanningAlgorithmCard';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { ApiResponse } from '../components/services/interfaces';
import { PlanningAlgorithmService } from '../components/services/PlanningAlgorithmService';
import { PlanningAlgorithmTemplate } from '../constants/PlanningAlgorithmTemplate';
import { PlanningAlgorithmFormValues } from '../constants/schemas';
import { IdentityErrorService } from '../helpers/IdentityErrorService';
import { useDebounce } from '../hooks';
import { ruLocale } from '../lang/ruLocale';
import { PlanningAlgorithm } from '../types/interfaces';
import pageSizeValues from '../ui/shared/paginator/pageSizeValues';
import { clearFilter } from '../utils';

export default function PlanningAlgorithms() {
  const [planningAlgorithms, setPlanningAlgorithms] = useState<
    PlanningAlgorithm[]
  >([]);
  const [selectedPlanningAlgorithm, setSelectedPlanningAlgorithm] =
    useState<PlanningAlgorithm>(PlanningAlgorithmTemplate);
  const [planningAlgorithmForm, setPlanningAlgorithmForm] =
    useState<PlanningAlgorithm>(PlanningAlgorithmTemplate);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string | number;
    name: string;
  } | null>(null);

  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [pageSize, setPageSize] = useState(
    pageSizeValues[pageSizeValues.length - 1].value
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [first, setFirst] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);

  const nameFilterRef = useRef<HTMLInputElement>(null);
  const descriptionFilterRef = useRef<HTMLInputElement>(null);
  const planningAlgorithmCardRef = useRef<null | { handleCancel: () => void }>(
    null
  );
  const toast = useRef<Toast>(null);

  const hasSelectedPlanningAlgorithm = isSelected(selectedPlanningAlgorithm);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
    setPageNumber(1);
    setFirst(0);
  }, []);

  const onPageChanged = useCallback((event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setPageSize(event.rows);
      setFirst(0);
    }
    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  const loadData = useCallback(() => {
    setLoading(true);
    const sort = multiSortMeta?.[0];
    const payload = JSON.stringify([
      {
        field: 'name',
        value: filters.name?.value,
        operator: filters.name?.matchMode
      },
      {
        field: 'description',
        value: filters.description?.value,
        operator: filters.description?.matchMode
      },
      {
        field: 'global',
        value: filters.global?.value,
        operator: filters.global?.matchMode
      }
    ]);

    PlanningAlgorithmService.getAll({
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: payload
    })
      .then((resp) => {
        if (resp.data) {
          setPlanningAlgorithms(resp.data.itemsList);
          setTotalCount(resp.data.total || 0);
        } else {
          setPlanningAlgorithms([]);
          setTotalCount(0);
        }
      })
      .catch(() => {
        setPlanningAlgorithms([]);
        setTotalCount(0);
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить список алгоритмов планирования.',
          life: 5000
        });
      })
      .finally(() => setLoading(false));
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const { debouncedCallback: debouncedLoadData } = useDebounce(loadData, 300);

  useEffect(() => {
    debouncedLoadData();
  }, [debouncedLoadData]);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setPageNumber(1);
    setFirst(0);
    setFilters(_filters);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setPageNumber(1);
    setFirst(0);
    setFilters(next);
  }, []);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const addButtonHandle = useCallback(() => {
    setPlanningAlgorithmForm(PlanningAlgorithmTemplate);
    setDialogVisible(true);
  }, []);

  const openPlanningAlgorithmCard = useCallback(
    async (algorithm: PlanningAlgorithm) => {
      const id = parseInt(String(algorithm.id), 10);
      if (!id) return;

      try {
        const resp = await PlanningAlgorithmService.getById(id);
        if (resp.data) {
          setPlanningAlgorithmForm(resp.data);
          setDialogVisible(true);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Ошибка',
            detail: 'Не удалось загрузить алгоритм планирования.',
            life: 5000
          });
        }
      } catch (error: unknown) {
        const status =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'status' in error.response
            ? Number((error.response as { status?: number }).status)
            : undefined;
        const detail =
          status === 500
            ? IdentityErrorService.getErrorMessage('Unknown')
            : 'Не удалось загрузить алгоритм планирования.';
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail,
          life: 5000
        });
      }
    },
    []
  );

  const editButtonHandle = () => {
    if (toast.current && !hasSelectedPlanningAlgorithm) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите строку для редактирования',
        life: 3000
      });
      return;
    }
    if (hasSelectedPlanningAlgorithm) {
      void openPlanningAlgorithmCard(selectedPlanningAlgorithm);
    }
  };

  const deleteButtonHandle = () => {
    if (toast.current && !hasSelectedPlanningAlgorithm) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите строку для удаления',
        life: 3000
      });
      return;
    }
    if (selectedPlanningAlgorithm?.id) {
      setDeleteTarget({
        id: selectedPlanningAlgorithm.id,
        name: String(selectedPlanningAlgorithm.name ?? '')
      });
      setDeleteDialogVisible(true);
    }
  };

  const handleDeleteConfirmation = () => {
    setSelectedPlanningAlgorithm(PlanningAlgorithmTemplate);
    void handleDelete(deleteTarget?.id ?? null);
    setDeleteTarget(null);
  };

  const handleDelete = async (
    id: string | number | null
  ): Promise<ApiResponse<null> | undefined> => {
    if (!id) return undefined;
    try {
      const resp = await PlanningAlgorithmService.delete(
        parseInt(String(id), 10)
      );
      const httpOk = resp.status >= 200 && resp.status < 300;
      const raw = resp.data as ApiResponse<null> | string | null | undefined;
      const payload =
        raw && typeof raw === 'object' && 'isSuccess' in raw ? raw : undefined;
      const explicitFailure =
        payload !== undefined && payload.isSuccess === false;

      if (httpOk && !explicitFailure) {
        toast.current?.show({
          severity: 'success',
          summary: 'Удалено',
          detail: 'Запись удалена.',
          life: 3000
        });
        setSelectedPlanningAlgorithm(PlanningAlgorithmTemplate);
        loadData();
        return (
          payload ?? {
            isSuccess: true,
            value: null,
            error: ''
          }
        );
      }

      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: payload?.error || 'Не удалось удалить запись.',
        life: 5000
      });
      return payload;
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось удалить запись.',
        life: 5000
      });
      return undefined;
    }
  };

  const handleAdd = async (
    algorithm: PlanningAlgorithmFormValues
  ): Promise<ApiResponse<PlanningAlgorithm> | undefined> => {
    try {
      const payload = {
        ...algorithm,
        id: 0,
        name: algorithm.name.trim(),
        description: algorithm.description ?? ''
      };
      const resp = await PlanningAlgorithmService.post(payload);
      const data = resp.data;
      if (data?.isSuccess) {
        toast.current?.show({
          severity: 'success',
          summary: 'Сохранено',
          detail: 'Алгоритм создан.',
          life: 3000
        });
        loadData();
      } else if (data?.error && data.error !== 'SameName') {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: data.error || 'Не удалось создать запись.',
          life: 5000
        });
      }
      return data;
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось создать запись.',
        life: 5000
      });
      return undefined;
    }
  };

  const handleUpdate = async (
    id: string | number,
    algorithm: PlanningAlgorithmFormValues
  ): Promise<ApiResponse<PlanningAlgorithm> | undefined> => {
    try {
      const numericId = parseInt(String(id), 10);
      const payload = {
        ...algorithm,
        id: numericId,
        name: algorithm.name.trim(),
        description: algorithm.description ?? ''
      };
      const resp = await PlanningAlgorithmService.put(numericId, payload);
      const data = resp.data;
      if (data?.isSuccess) {
        toast.current?.show({
          severity: 'success',
          summary: 'Сохранено',
          detail: 'Изменения сохранены.',
          life: 3000
        });
        loadData();
      } else if (data?.error && data.error !== 'SameName') {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: data.error || 'Не удалось сохранить запись.',
          life: 5000
        });
      }
      return data;
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось сохранить запись.',
        life: 5000
      });
      return undefined;
    }
  };

  const closeDialog = () => {
    setDialogVisible(false);
  };

  const onHidePlanningAlgorithmCard = () => {
    if (planningAlgorithmCardRef.current !== null) {
      planningAlgorithmCardRef.current.handleCancel();
    } else {
      closeDialog();
    }
  };

  const header = (
    <TablePageHeader
      tableName="planningAlgorithms"
      onGlobalSearchChange={(value) =>
        handleInputChange('global')({
          target: { value }
        } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
    />
  );

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    const rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  return (
    <div className="planningAlgorithms-table wrapper wrapper--main">
      <Toast ref={toast} />

      <Dialog
        header={
          planningAlgorithmForm.id !== '' &&
          planningAlgorithmForm.id !== undefined &&
          planningAlgorithmForm.id !== null &&
          planningAlgorithmForm.id !== 0
            ? 'Редактирование алгоритма'
            : 'Новый алгоритм'
        }
        visible={dialogVisible}
        onHide={() => onHidePlanningAlgorithmCard()}
        style={{
          minWidth: '743px',
          width: '743px',
          height: 'fit-content'
        }}
        contentStyle={{
          display: 'flex',
          overflow: 'hidden',
          minHeight: 'fit-content',
          paddingTop: 0
        }}
        modal
      >
        <VisibilityWrapper code="planningAlgorithms-card__roles">
          <PlanningAlgorithmCard
            algorithmProps={planningAlgorithmForm}
            existingAlgorithms={planningAlgorithms}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={planningAlgorithmCardRef}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="алгоритм планирования"
        entityName={deleteTarget?.name}
      />

      <DataTable
        filters={filters}
        lazy
        paginator
        first={first}
        rows={pageSize}
        totalRecords={totalCount}
        onPage={onPageChanged}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        paginatorLeft={' '}
        paginatorRight={`Всего: ${
          planningAlgorithms.length > 0 ? totalCount : 0
        }`}
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedPlanningAlgorithm}
        onSelectionChange={(e) =>
          setSelectedPlanningAlgorithm(e.value as PlanningAlgorithm)
        }
        onRowDoubleClick={(e) => {
          void openPlanningAlgorithmCard(e.data as PlanningAlgorithm);
        }}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        value={planningAlgorithms}
        dataKey="id"
        filterDisplay="row"
        header={header}
        emptyMessage="Записи не найдены."
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
        loading={loading}
        globalFilterFields={['name', 'description']}
      >
        <Column
          columnKey="name"
          sortable
          field="name"
          header="Название"
          filter
          onFilterClear={() =>
            clearFilter(
              'name',
              filters,
              setFilters,
              nameFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={nameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по названию"
              />
            </div>
          }
          style={{ width: '35%' }}
        />
        <Column
          columnKey="description"
          sortable
          field="description"
          header="Описание"
          filter
          onFilterClear={() =>
            clearFilter(
              'description',
              filters,
              setFilters,
              descriptionFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={descriptionFilterRef}
                onChange={handleInputChange('description')}
                placeholder="Поиск по описанию"
              />
            </div>
          }
          style={{ width: '65%' }}
        />
      </DataTable>
    </div>
  );
}
